import { toast } from 'sonner';
import { startCase } from 'lodash';
import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import { Box, Card, Stack, Button, TextField, Typography, DialogContent, DialogActions } from '@mui/material';

import { fData } from 'src/utils/format-number';

import { sanitizeAndValidate } from 'src/utils';
import { useOrganisation } from 'src/organisation';
import { fetcher, endpoints } from 'src/lib/axios';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------
// Profile tab of the Account Settings dialog — real user data + avatar + reset password
// (via useOrganisation()). Owns its own state and footer; the reset-password screen takes over the
// whole tab, so it reports that up (onResetActiveChange) to let the dialog hide the tabs bar.
// ----------------------------------------------------------------------

type ProfileTabProps = {
  onClose: () => void;
  /** True while the reset-password confirmation screen is showing (dialog hides its tabs bar). */
  onResetActiveChange: (active: boolean) => void;
};

const TextOverflowStyle = {
  maxWidth: 240,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Card + large-field styling tuned to the mock (rounded panels, tall inputs).
const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '18px',
  boxShadow: '0 2px 12px rgba(27,27,33,0.05)',
} as const;
const bigFieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '14px' },
  '& .MuiOutlinedInput-input': { fontSize: 18, py: '15px' },
} as const;

export function ProfileTab({ onClose, onResetActiveChange }: ProfileTabProps) {
  const profileDetails = useOrganisation();
  const {
    firstName: FirstName,
    lastName: LastName,
    displayName,
    email,
    profilePictureUrl,
    updateProfile,
    updateProfilePicture,
  } = profileDetails ?? ({} as any);

  const [inputFields, setInputFields] = useState({ firstName: '', lastName: '' });
  const [inputErrors, setInputErrors] = useState({ firstName: '', lastName: '' });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const resetScreen = useBoolean();

  useEffect(() => {
    setInputFields({ firstName: FirstName || '', lastName: LastName || '' });
  }, [FirstName, LastName]);

  useEffect(() => {
    setAvatarUrl(profilePictureUrl || null);
  }, [profilePictureUrl]);

  useEffect(() => {
    onResetActiveChange(resetScreen.value);
  }, [resetScreen.value, onResetActiveChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'firstName' | 'lastName') => {
    const { value } = e.target;
    setInputFields((prev) => ({ ...prev, [field]: value }));
    if (!value.trim() && field === 'firstName') {
      setInputErrors((prev) => ({ ...prev, [field]: `${startCase(field)} is required` }));
      return;
    }
    const { isValid, message } = sanitizeAndValidate(value, 'name', field !== 'lastName');
    setInputErrors((prev) => ({ ...prev, [field]: isValid ? '' : message }));
  };

  const handleSaveProfile = () => {
    setIsUpdating(true);
    updateProfile(
      { firstName: inputFields.firstName, lastName: inputFields.lastName },
      (_response: any, success: boolean) => {
        if (success) onClose();
        setIsUpdating(false);
      }
    );
  };

  const handlePhotoFile = (file: File) => {
    if (file.size > 2000000) {
      toast.error('File too large', { description: `File is larger than ${fData(2000000)}` });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    updateProfilePicture(formData);
  };

  const handleRemoveAvatar = () => {
    profileDetails?.deleteProfilePicture((_response: any, success: boolean) => {
      if (success) setAvatarUrl(null);
    });
  };

  const handleResetPassword = () => {
    fetcher(endpoints.account.resetPassword)
      .then(() => {
        resetScreen.onTrue();
        setInputErrors({ firstName: '', lastName: '' });
      })
      .catch(() => {
        toast.error('Failed', { description: 'Failed to reset password' });
      });
  };

  return (
    <>
      {/* Fixed height so switching Profile ↔ Notifications never resizes the dialog (no jump). */}
      <DialogContent sx={{ height: 'min(70vh, 620px)', overflowY: 'auto' }}>
        {resetScreen.value ? (
          <ResetPasswordScreen />
        ) : (
          <Stack spacing={2.5} sx={{ my: 1 }}>
            {/* Photo + identity — full-width card, upload box centred (mock layout) */}
            <Card sx={{ ...cardSx, p: '30px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <PhotoUpload value={avatarUrl} onFile={handlePhotoFile} onRemove={handleRemoveAvatar} />
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', mt: 2.5, ...TextOverflowStyle }}>
                {displayName || '-'}
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: 'text.disabled', mt: 0.5, ...TextOverflowStyle }}>
                {email || '-'}
              </Typography>
            </Card>

            {/* Name + reset — full-width card with tall fields and a full-width reset */}
            <Card sx={{ ...cardSx, p: '28px 26px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                fullWidth
                label="First Name"
                value={inputFields.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'firstName')}
                required
                error={!!inputErrors.firstName}
                helperText={inputErrors.firstName}
                sx={bigFieldSx}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={inputFields.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'lastName')}
                inputProps={{ maxLength: 50 }}
                error={!!inputErrors.lastName}
                helperText={inputErrors.lastName}
                sx={bigFieldSx}
              />
              <Button
                fullWidth
                variant="soft"
                color="primary"
                endIcon={<Iconify icon="material-symbols:lock-reset-outline-rounded" />}
                onClick={handleResetPassword}
                sx={{ justifyContent: 'space-between', px: '20px', py: '14px', borderRadius: '14px', fontSize: 16, fontWeight: 700, mt: 0.5 }}
              >
                <span>Reset My Password</span>
              </Button>
            </Card>
          </Stack>
        )}
      </DialogContent>

      {!resetScreen.value && (
        <DialogActions>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleSaveProfile}
            loading={isUpdating}
            size="large"
            sx={{ borderRadius: '14px', px: 3.5, fontWeight: 700 }}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      )}
    </>
  );
}

/* --------------------------------------------------------------- photo upload */

function PhotoUpload({
  value,
  onFile,
  onRemove,
}: {
  value: string | null;
  onFile: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        sx={{
          width: 190,
          height: 200,
          p: 1.5,
          borderRadius: 2.5,
          border: '2px dashed',
          borderColor: 'divider',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'border-color .15s ease',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {value ? (
          <Box
            component="img"
            src={value}
            alt="Profile"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1.5, display: 'block' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'background.neutral',
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                bgcolor: 'background.paper',
                boxShadow: (t) => t.customShadows?.z8 ?? 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon="material-symbols:photo-camera-outline-rounded" width={22} sx={{ color: 'text.secondary' }} />
            </Box>
            <Typography sx={{ fontSize: 15, color: 'text.disabled', fontWeight: 500 }}>Upload photo</Typography>
          </Box>
        )}
      </Box>

      {value && (
        <Button size="small" color="error" onClick={onRemove} sx={{ minWidth: 0 }}>
          Remove
        </Button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </Box>
  );
}

/* --------------------------------------------------------------- reset screen */

function ResetPasswordScreen() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', py: 2 }}>
      <Iconify icon="mingcute:send-plane-fill" width={160} height={160} sx={{ color: 'primary.main' }} />
      <Typography variant="h3" sx={{ textAlign: 'center' }}>
        We&apos;ve sent you a link to reset your password.
      </Typography>
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        Just follow the instructions in the email to set a new one.
      </Typography>
      <Typography variant="subtitle2" color="text.secondary">
        Didn&apos;t receive it? Check your spam folder
      </Typography>
    </Box>
  );
}
